// ---------------------------------------------------------------------------
// cities/paris.content.ts — Paris city landing page content (9 locales)
// ---------------------------------------------------------------------------

import type { CityRecord } from './types';

export const PARIS_CITY: CityRecord = {
  constants: {
    slug: 'hotel-software-paris',
    stats: {
      hotelCount: '2,000+',
      avgRate: 'EUR 220',
      occupancyRate: '80%',
      peakSeason: 'Apr - Oct',
    },
  },
  content: {
    /* ------------------------------------------------------------------ */
    /*  EN — English                                                       */
    /* ------------------------------------------------------------------ */
    en: {
      cityName: 'Paris',
      country: 'France',
      heroTitle: 'Hotel Software for Paris Hospitality',
      heroSubtitle:
        "Elegant PMS for Parisian hotels, maisons d'hotes, and luxury properties. Euro billing, taxe de sejour automation, and French regulatory compliance.",
      highlights: [
        {
          title: 'Taxe de Sejour Automation',
          description:
            'Automated tourist tax calculation based on hotel classification. Generate city-mandated reports and track collections per guest stay.',
        },
        {
          title: 'French Regulatory Compliance',
          description:
            'Police fiche generation, RGPD (French GDPR) data handling, and Atout France classification support built into every workflow.',
        },
        {
          title: 'Luxury Guest Experience',
          description:
            'Concierge module, guest preference tracking, and personalized pre-arrival communications for discerning Parisian hotel guests.',
        },
        {
          title: 'Euro & Multi-Currency',
          description:
            'Native EUR billing with support for USD, GBP, CHF, and 20+ currencies. Automated TVA (VAT) at 10% for accommodation services.',
        },
      ],
      propertyTypes: ['Palace Hotels', 'Boutique Hotels', "Maisons d'Hotes", 'Aparthotels', 'Design Hotels', 'Budget Hotels'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Taxe de sejour automation', 'Police fiche generation', '10% TVA on accommodation', 'RGPD data protection', 'Atout France classification support'],
      faqItems: [
        {
          question: 'Does HotelStack.io handle the Paris taxe de sejour?',
          answer:
            'Yes. Our system automatically calculates the taxe de sejour based on your hotel star classification and generates the required reports for city authorities. Both flat-rate and proportional methods are supported.',
        },
        {
          question: 'Can it generate police fiche forms for guest registration?',
          answer:
            'Absolutely. HotelStack.io captures required guest identity information at check-in and generates police fiche forms in the mandated format for submission to local authorities.',
        },
        {
          question: 'Is the interface available in French?',
          answer:
            'Yes. The full PMS interface, guest-facing booking engine, and all reports are available in French. Staff can switch between French and English at any time.',
        },
        {
          question: 'How does TVA (VAT) work for Paris hotels?',
          answer:
            'The platform automatically applies 10% TVA on accommodation and 20% TVA on other services. All invoices comply with French tax requirements and are ready for accounting software integration.',
        },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  AR — Arabic                                                        */
    /* ------------------------------------------------------------------ */
    ar: {
      cityName: 'باريس',
      country: 'فرنسا',
      heroTitle: 'برنامج إدارة الفنادق في باريس',
      heroSubtitle:
        "نظام إدارة فندقي أنيق للفنادق الباريسية و Maisons d'Hotes والعقارات الفاخرة. فوترة باليورو، أتمتة taxe de sejour، والامتثال التنظيمي الفرنسي.",
      highlights: [
        {
          title: 'أتمتة Taxe de Sejour',
          description:
            'حساب تلقائي لضريبة السياحة بناءً على تصنيف الفندق. إنشاء التقارير المطلوبة من البلدية وتتبع التحصيلات لكل إقامة.',
        },
        {
          title: 'الامتثال التنظيمي الفرنسي',
          description:
            'إنشاء police fiche، معالجة البيانات وفق RGPD، ودعم تصنيف Atout France مدمج في كل سير عمل.',
        },
        {
          title: 'تجربة ضيوف فاخرة',
          description:
            'وحدة الكونسيرج، تتبع تفضيلات الضيوف، واتصالات شخصية قبل الوصول للضيوف المميزين في فنادق باريس.',
        },
        {
          title: 'اليورو والعملات المتعددة',
          description:
            'فوترة أصلية باليورو مع دعم أكثر من ٢٠ عملة. تطبيق تلقائي لـ TVA بنسبة ١٠% على خدمات الإقامة.',
        },
      ],
      propertyTypes: ['فنادق القصور', 'فنادق بوتيك', "Maisons d'Hotes", 'شقق فندقية', 'فنادق تصميمية', 'فنادق اقتصادية'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['أتمتة taxe de sejour', 'إنشاء police fiche', 'TVA ١٠% على الإقامة', 'حماية البيانات RGPD', 'دعم تصنيف Atout France'],
      faqItems: [
        {
          question: 'هل يتعامل HotelStack.io مع taxe de sejour في باريس؟',
          answer: 'نعم. يحسب نظامنا تلقائياً taxe de sejour بناءً على تصنيف النجوم وينشئ التقارير المطلوبة للسلطات. يدعم الطريقتين الثابتة والنسبية.',
        },
        {
          question: 'هل يمكنه إنشاء نماذج police fiche لتسجيل الضيوف؟',
          answer: 'بالتأكيد. يلتقط HotelStack.io بيانات هوية الضيف المطلوبة عند تسجيل الدخول وينشئ نماذج police fiche بالصيغة المعتمدة.',
        },
        {
          question: 'هل الواجهة متاحة بالفرنسية؟',
          answer: 'نعم. واجهة النظام الكاملة ومحرك الحجز والتقارير متاحة بالفرنسية. يمكن للموظفين التبديل بين الفرنسية والإنجليزية في أي وقت.',
        },
        {
          question: 'كيف يعمل TVA لفنادق باريس؟',
          answer: 'يطبق النظام تلقائياً TVA ١٠% على الإقامة و٢٠% على الخدمات الأخرى. جميع الفواتير متوافقة مع المتطلبات الضريبية الفرنسية.',
        },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  TH — Thai                                                          */
    /* ------------------------------------------------------------------ */
    th: {
      cityName: 'ปารีส',
      country: 'ฝรั่งเศส',
      heroTitle: 'ซอฟต์แวร์บริหารโรงแรมสำหรับปารีส',
      heroSubtitle:
        "ระบบ PMS สำหรับโรงแรมในปารีส, Maisons d'Hotes และที่พักหรู การออกบิลสกุลยูโร ระบบ taxe de sejour อัตโนมัติ และการปฏิบัติตามกฎระเบียบฝรั่งเศส",
      highlights: [
        { title: 'ระบบ Taxe de Sejour อัตโนมัติ', description: 'คำนวณภาษีนักท่องเที่ยวอัตโนมัติตามระดับดาวของโรงแรม สร้างรายงานตามที่เทศบาลกำหนดและติดตามการเก็บเงินต่อการเข้าพัก' },
        { title: 'การปฏิบัติตามกฎระเบียบฝรั่งเศส', description: 'สร้าง police fiche, จัดการข้อมูลตาม RGPD และรองรับการจัดระดับ Atout France ในทุกขั้นตอนการทำงาน' },
        { title: 'ประสบการณ์แขกระดับหรู', description: 'โมดูลคอนเซียร์จ ติดตามความชอบของแขก และการสื่อสารส่วนบุคคลก่อนเข้าพักสำหรับแขกโรงแรมในปารีส' },
        { title: 'ยูโรและหลายสกุลเงิน', description: 'ออกบิลสกุล EUR พร้อมรองรับกว่า 20 สกุลเงิน คำนวณ TVA 10% สำหรับบริการที่พักอัตโนมัติ' },
      ],
      propertyTypes: ['โรงแรมพาเลซ', 'โรงแรมบูทีค', "Maisons d'Hotes", 'อพาร์ทโฮเทล', 'โรงแรมดีไซน์', 'โรงแรมประหยัด'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['ระบบ taxe de sejour อัตโนมัติ', 'สร้าง police fiche', 'TVA 10% สำหรับที่พัก', 'การคุ้มครองข้อมูล RGPD', 'รองรับการจัดระดับ Atout France'],
      faqItems: [
        { question: 'HotelStack.io จัดการ taxe de sejour ของปารีสได้หรือไม่?', answer: 'ได้ ระบบคำนวณ taxe de sejour อัตโนมัติตามระดับดาวและสร้างรายงานที่จำเป็นให้หน่วยงาน รองรับทั้งแบบอัตราคงที่และแบบสัดส่วน' },
        { question: 'สามารถสร้างแบบฟอร์ม police fiche ได้หรือไม่?', answer: 'ได้ HotelStack.io บันทึกข้อมูลประจำตัวแขกที่จำเป็นเมื่อเช็คอินและสร้างแบบฟอร์ม police fiche ในรูปแบบที่กำหนด' },
        { question: 'อินเทอร์เฟซมีภาษาฝรั่งเศสหรือไม่?', answer: 'มี ระบบ PMS ทั้งหมด ระบบจองสำหรับแขก และรายงานทั้งหมดมีภาษาฝรั่งเศส พนักงานสามารถสลับระหว่างภาษาฝรั่งเศสและอังกฤษได้ตลอดเวลา' },
        { question: 'TVA ทำงานอย่างไรสำหรับโรงแรมในปารีส?', answer: 'ระบบใช้ TVA 10% สำหรับที่พักและ 20% สำหรับบริการอื่นอัตโนมัติ ใบแจ้งหนี้ทั้งหมดเป็นไปตามข้อกำหนดภาษีฝรั่งเศส' },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  ES — Spanish                                                       */
    /* ------------------------------------------------------------------ */
    es: {
      cityName: 'Paris',
      country: 'Francia',
      heroTitle: 'Software Hotelero para la Hospitalidad Parisina',
      heroSubtitle:
        "PMS elegante para hoteles parisinos, Maisons d'Hotes y propiedades de lujo. Facturación en euros, automatización de taxe de sejour y cumplimiento normativo francés.",
      highlights: [
        { title: 'Automatización de Taxe de Sejour', description: 'Cálculo automático del impuesto turístico según la clasificación del hotel. Generación de informes municipales y seguimiento de cobros por estancia.' },
        { title: 'Cumplimiento Normativo Francés', description: 'Generación de police fiche, manejo de datos según RGPD y soporte de clasificación Atout France integrado en cada flujo de trabajo.' },
        { title: 'Experiencia de Lujo para Huéspedes', description: 'Módulo de conserjería, seguimiento de preferencias y comunicaciones personalizadas previas a la llegada para huéspedes exigentes.' },
        { title: 'Euro y Multidivisa', description: 'Facturación nativa en EUR con soporte para más de 20 monedas. TVA automático del 10% en servicios de alojamiento.' },
      ],
      propertyTypes: ['Hoteles Palacio', 'Hoteles Boutique', "Maisons d'Hotes", 'Aparthoteles', 'Hoteles de Diseño', 'Hoteles Económicos'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Automatización de taxe de sejour', 'Generación de police fiche', '10% TVA en alojamiento', 'Protección de datos RGPD', 'Soporte de clasificación Atout France'],
      faqItems: [
        { question: '¿HotelStack.io gestiona la taxe de sejour de Paris?', answer: 'Sí. Nuestro sistema calcula automáticamente la taxe de sejour según la clasificación por estrellas y genera los informes requeridos. Se admiten métodos de tarifa fija y proporcional.' },
        { question: '¿Puede generar formularios police fiche?', answer: 'Por supuesto. HotelStack.io captura la información de identidad requerida al check-in y genera formularios police fiche en el formato establecido.' },
        { question: '¿La interfaz está disponible en francés?', answer: 'Sí. La interfaz completa del PMS, el motor de reservas y todos los informes están disponibles en francés. El personal puede alternar entre francés e inglés en cualquier momento.' },
        { question: '¿Cómo funciona el TVA para hoteles en Paris?', answer: 'La plataforma aplica automáticamente 10% de TVA en alojamiento y 20% en otros servicios. Todas las facturas cumplen con los requisitos fiscales franceses.' },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  FR — French                                                        */
    /* ------------------------------------------------------------------ */
    fr: {
      cityName: 'Paris',
      country: 'France',
      heroTitle: "Logiciel Hôtelier pour l'Hospitalité Parisienne",
      heroSubtitle:
        "PMS élégant pour hôtels parisiens, maisons d'hôtes et établissements de prestige. Facturation en euros, automatisation de la taxe de séjour et conformité réglementaire française.",
      highlights: [
        { title: 'Automatisation de la Taxe de Séjour', description: "Calcul automatique de la taxe de séjour selon le classement de l'établissement. Génération des déclarations obligatoires et suivi des perceptions par séjour." },
        { title: 'Conformité Réglementaire Française', description: "Génération des fiches de police, traitement des données conforme au RGPD et prise en charge du classement Atout France intégrés à chaque processus." },
        { title: "Expérience Client d'Exception", description: "Module conciergerie, suivi des préférences clients et communications personnalisées avant l'arrivée pour une clientèle parisienne exigeante." },
        { title: 'Euro et Multidevise', description: "Facturation native en EUR avec prise en charge de plus de 20 devises. Application automatique de la TVA à 10 % sur l'hébergement." },
      ],
      propertyTypes: ['Palaces', 'Hôtels Boutique', "Maisons d'Hôtes", 'Aparthôtels', 'Hôtels Design', 'Hôtels Économiques'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Élysées', 'Montmartre', 'Opéra', 'Bastille', 'Quartier Latin'],
      compliance: ['Automatisation de la taxe de séjour', 'Génération des fiches de police', "TVA à 10 % sur l'hébergement", 'Protection des données RGPD', 'Prise en charge du classement Atout France'],
      faqItems: [
        { question: 'HotelStack.io gère-t-il la taxe de séjour parisienne ?', answer: "Oui. Notre système calcule automatiquement la taxe de séjour en fonction du classement en étoiles de votre établissement et génère les déclarations requises par la collectivité. Les méthodes au forfait et au réel sont prises en charge." },
        { question: 'Peut-il générer les fiches de police pour le registre des voyageurs ?', answer: "Absolument. HotelStack.io recueille les informations d'identité requises lors de l'enregistrement et génère les fiches de police au format réglementaire pour transmission aux autorités." },
        { question: "L'interface est-elle disponible en français ?", answer: "Oui. L'intégralité de l'interface PMS, du moteur de réservation en ligne et des états sont disponibles en français. Le personnel peut basculer entre le français et l'anglais à tout moment." },
        { question: 'Comment fonctionne la TVA pour les hôtels parisiens ?', answer: "La plateforme applique automatiquement la TVA à 10 % sur l'hébergement et à 20 % sur les autres prestations. Toutes les factures sont conformes aux exigences fiscales françaises et prêtes pour l'intégration comptable." },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  DE — German                                                        */
    /* ------------------------------------------------------------------ */
    de: {
      cityName: 'Paris',
      country: 'Frankreich',
      heroTitle: 'Hotelsoftware für Pariser Gastfreundschaft',
      heroSubtitle:
        "Elegantes PMS für Pariser Hotels, Maisons d'Hotes und Luxusunterkünfte. Euro-Abrechnung, automatisierte Taxe de Sejour und französische Regulierungskonformität.",
      highlights: [
        { title: 'Taxe de Sejour Automatisierung', description: 'Automatische Tourismussteuerberechnung basierend auf der Hotelklassifizierung. Erstellung vorgeschriebener Berichte und Nachverfolgung der Erhebungen pro Aufenthalt.' },
        { title: 'Französische Regulierungskonformität', description: 'Police-Fiche-Erstellung, RGPD-konforme Datenverarbeitung und Atout-France-Klassifizierungsunterstützung in jedem Arbeitsablauf.' },
        { title: 'Luxuriöses Gästeerlebnis', description: 'Concierge-Modul, Gästepräferenzverfolgung und personalisierte Voranreisekommunikation für anspruchsvolle Pariser Hotelgäste.' },
        { title: 'Euro und Mehrwährung', description: 'Native EUR-Abrechnung mit Unterstützung für über 20 Währungen. Automatische TVA von 10 % auf Beherbergungsleistungen.' },
      ],
      propertyTypes: ['Palasthotels', 'Boutique-Hotels', "Maisons d'Hotes", 'Aparthotels', 'Designhotels', 'Budget-Hotels'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Taxe de Sejour Automatisierung', 'Police-Fiche-Erstellung', '10 % TVA auf Beherbergung', 'RGPD-Datenschutz', 'Atout-France-Klassifizierungsunterstützung'],
      faqItems: [
        { question: 'Verwaltet HotelStack.io die Pariser Taxe de Sejour?', answer: 'Ja. Unser System berechnet die Taxe de Sejour automatisch nach Sterneklassifizierung und erstellt die vorgeschriebenen Berichte. Pauschal- und proportionale Methoden werden unterstützt.' },
        { question: 'Können Police-Fiche-Formulare erstellt werden?', answer: 'Selbstverständlich. HotelStack.io erfasst die erforderlichen Identitätsdaten beim Check-in und erstellt Police-Fiche-Formulare im vorgeschriebenen Format.' },
        { question: 'Ist die Oberfläche auf Französisch verfügbar?', answer: 'Ja. Die vollständige PMS-Oberfläche, die Buchungsmaschine und alle Berichte sind auf Französisch verfügbar. Mitarbeiter können jederzeit zwischen Französisch und Englisch wechseln.' },
        { question: 'Wie funktioniert die TVA für Pariser Hotels?', answer: 'Die Plattform wendet automatisch 10 % TVA auf Beherbergung und 20 % auf sonstige Leistungen an. Alle Rechnungen entsprechen den französischen Steueranforderungen.' },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  JA — Japanese                                                      */
    /* ------------------------------------------------------------------ */
    ja: {
      cityName: 'パリ',
      country: 'フランス',
      heroTitle: 'パリのホスピタリティ向けホテルソフトウェア',
      heroSubtitle:
        "パリのホテル、Maisons d'Hotes、高級施設向けの洗練されたPMS。ユーロ請求、taxe de sejour自動化、フランス規制準拠。",
      highlights: [
        { title: 'Taxe de Sejour 自動化', description: 'ホテル格付けに基づく観光税の自動計算。市が義務付けるレポート生成と滞在ごとの徴収追跡。' },
        { title: 'フランス規制準拠', description: 'Police fiche生成、RGPDデータ処理、Atout France格付け対応がすべてのワークフローに組み込まれています。' },
        { title: 'ラグジュアリーゲスト体験', description: 'コンシェルジュモジュール、ゲスト嗜好追跡、パリの上質なホテルゲスト向けのパーソナライズされた到着前コミュニケーション。' },
        { title: 'ユーロ・マルチ通貨対応', description: 'EUR建て請求を標準装備し、20以上の通貨に対応。宿泊サービスへのTVA 10%自動適用。' },
      ],
      propertyTypes: ['パレスホテル', 'ブティックホテル', "Maisons d'Hotes", 'アパートホテル', 'デザインホテル', 'バジェットホテル'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Taxe de sejour 自動化', 'Police fiche 生成', '宿泊への TVA 10%', 'RGPD データ保護', 'Atout France 格付け対応'],
      faqItems: [
        { question: 'HotelStack.ioはパリのtaxe de sejourに対応していますか？', answer: 'はい。星格付けに基づいてtaxe de sejourを自動計算し、当局向けの報告書を生成します。定額方式と按分方式の両方に対応しています。' },
        { question: 'Police ficheフォームを生成できますか？', answer: 'もちろんです。HotelStack.ioはチェックイン時に必要な身分情報を取得し、所定の形式でpolice ficheフォームを生成します。' },
        { question: 'フランス語のインターフェースはありますか？', answer: 'はい。PMS全体、予約エンジン、全レポートがフランス語で利用できます。スタッフはいつでもフランス語と英語を切り替えられます。' },
        { question: 'パリのホテルでTVAはどう機能しますか？', answer: 'プラットフォームは宿泊にTVA 10%、その他サービスにTVA 20%を自動適用します。すべての請求書はフランスの税要件に準拠しています。' },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  ZH — Chinese Simplified                                            */
    /* ------------------------------------------------------------------ */
    zh: {
      cityName: '巴黎',
      country: '法国',
      heroTitle: '巴黎酒店管理软件',
      heroSubtitle:
        "为巴黎酒店、Maisons d'Hotes及奢华物业打造的优雅PMS。欧元计费、taxe de sejour自动化及法国法规合规。",
      highlights: [
        { title: 'Taxe de Sejour 自动化', description: '根据酒店星级自动计算旅游税。生成市政要求的报告并按住宿跟踪征收情况。' },
        { title: '法国法规合规', description: 'Police fiche生成、RGPD数据处理及Atout France评级支持内置于每个工作流程中。' },
        { title: '奢华宾客体验', description: '礼宾模块、宾客偏好追踪及个性化抵达前沟通，服务巴黎酒店的尊贵宾客。' },
        { title: '欧元及多币种', description: '原生EUR计费，支持20余种货币。住宿服务自动适用10% TVA。' },
      ],
      propertyTypes: ['宫殿酒店', '精品酒店', "Maisons d'Hotes", '公寓式酒店', '设计酒店', '经济酒店'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Taxe de sejour 自动化', 'Police fiche 生成', '住宿10% TVA', 'RGPD 数据保护', 'Atout France 评级支持'],
      faqItems: [
        { question: 'HotelStack.io能处理巴黎的taxe de sejour吗？', answer: '能。系统根据酒店星级自动计算taxe de sejour并生成当局要求的报告。支持固定费率和按比例两种方式。' },
        { question: '能生成police fiche表格吗？', answer: '当然可以。HotelStack.io在入住时采集所需身份信息，并按规定格式生成police fiche表格。' },
        { question: '界面有法语版本吗？', answer: '有。完整的PMS界面、预订引擎和所有报告均提供法语版本。员工可随时在法语和英语之间切换。' },
        { question: '巴黎酒店的TVA如何运作？', answer: '平台自动对住宿适用10% TVA，对其他服务适用20% TVA。所有发票符合法国税务要求。' },
      ],
    },
    /* ------------------------------------------------------------------ */
    /*  HI — Hindi                                                         */
    /* ------------------------------------------------------------------ */
    hi: {
      cityName: 'पेरिस',
      country: 'फ़्रांस',
      heroTitle: 'पेरिस आतिथ्य के लिए होटल सॉफ़्टवेयर',
      heroSubtitle:
        "पेरिस के होटलों, Maisons d'Hotes और लक्ज़री संपत्तियों के लिए शानदार PMS। यूरो बिलिंग, taxe de sejour स्वचालन और फ़्रांसीसी नियामक अनुपालन।",
      highlights: [
        { title: 'Taxe de Sejour स्वचालन', description: 'होटल वर्गीकरण के आधार पर पर्यटन कर की स्वचालित गणना। नगरपालिका द्वारा अनिवार्य रिपोर्ट तैयार करें और प्रति ठहराव संग्रह ट्रैक करें।' },
        { title: 'फ़्रांसीसी नियामक अनुपालन', description: 'Police fiche निर्माण, RGPD डेटा प्रबंधन और Atout France वर्गीकरण समर्थन हर कार्यप्रवाह में अंतर्निहित।' },
        { title: 'लक्ज़री अतिथि अनुभव', description: 'कंसीयर्ज मॉड्यूल, अतिथि प्राथमिकता ट्रैकिंग और पेरिस के विशिष्ट होटल अतिथियों के लिए व्यक्तिगत आगमन-पूर्व संचार।' },
        { title: 'यूरो और बहु-मुद्रा', description: 'EUR में मूल बिलिंग के साथ 20+ मुद्राओं का समर्थन। आवास सेवाओं पर 10% TVA का स्वचालित अनुप्रयोग।' },
      ],
      propertyTypes: ['पैलेस होटल', 'बुटीक होटल', "Maisons d'Hotes", 'अपार्टहोटल', 'डिज़ाइन होटल', 'बजट होटल'],
      popularAreas: ['Le Marais', 'Saint-Germain', 'Champs-Elysees', 'Montmartre', 'Opera', 'Bastille', 'Latin Quarter'],
      compliance: ['Taxe de sejour स्वचालन', 'Police fiche निर्माण', 'आवास पर 10% TVA', 'RGPD डेटा सुरक्षा', 'Atout France वर्गीकरण समर्थन'],
      faqItems: [
        { question: 'क्या HotelStack.io पेरिस की taxe de sejour संभालता है?', answer: 'हाँ। हमारा सिस्टम स्टार वर्गीकरण के आधार पर taxe de sejour की स्वचालित गणना करता है और अधिकारियों के लिए आवश्यक रिपोर्ट तैयार करता है। फ़्लैट-रेट और आनुपातिक दोनों विधियाँ समर्थित हैं।' },
        { question: 'क्या यह अतिथि पंजीकरण के लिए police fiche फ़ॉर्म बना सकता है?', answer: 'बिल्कुल। HotelStack.io चेक-इन पर आवश्यक पहचान जानकारी एकत्र करता है और निर्धारित प्रारूप में police fiche फ़ॉर्म तैयार करता है।' },
        { question: 'क्या इंटरफ़ेस फ़्रांसीसी में उपलब्ध है?', answer: 'हाँ। पूर्ण PMS इंटरफ़ेस, बुकिंग इंजन और सभी रिपोर्ट फ़्रांसीसी में उपलब्ध हैं। कर्मचारी किसी भी समय फ़्रांसीसी और अंग्रेज़ी के बीच स्विच कर सकते हैं।' },
        { question: 'पेरिस के होटलों में TVA कैसे काम करता है?', answer: 'प्लेटफ़ॉर्म स्वचालित रूप से आवास पर 10% TVA और अन्य सेवाओं पर 20% TVA लागू करता है। सभी चालान फ़्रांसीसी कर आवश्यकताओं का अनुपालन करते हैं।' },
      ],
    },
  },
};
